import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import ToastBody from "@/components/ui/toast-body";
import { IconButton } from "@/components/ui/icon-button";
import { Close } from "@dreamhub/designsystem/icons";
import { isWithdrawVersionNotPending } from "./withdrawError";
import { useWithdrawTermsVersion } from "./hooks";
import { TermsVersionSummary } from "./types";

interface DiscardAmendment {
  target: TermsVersionSummary | null;
  isDiscarding: boolean;
  request: (version: TermsVersionSummary) => void;
  keep: () => void;
  confirm: () => Promise<void>;
}

export const useDiscardAmendment = (contractId: string): DiscardAmendment => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const withdrawVersion = useWithdrawTermsVersion();
  // The result object churns on each mutation state change; mutateAsync doesn't.
  const withdrawVersionRef = useRef(withdrawVersion);
  withdrawVersionRef.current = withdrawVersion;
  const [target, setTarget] = useState<TermsVersionSummary | null>(null);
  const [isDiscarding, setIsDiscarding] = useState(false);

  // One key serves the timeline, the header badge and the kind picker's note.
  // The contract read is left alone: a withdrawn amendment never took effect.
  const refetchVersions = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["contracts/terms/versions", { pathParams: { contractId } }]
    });
    // Prefix match: View terms reads per-version detail under its own key.
    queryClient.invalidateQueries({
      queryKey: ["contracts/terms/versionDetail"]
    });
  }, [queryClient, contractId]);

  const confirm = useCallback(async () => {
    if (!target || isDiscarding) return;
    setIsDiscarding(true);
    try {
      await withdrawVersionRef.current.mutateAsync({
        pathParams: { contractId, versionId: target.versionId }
      });
      setTarget(null);
      refetchVersions();
      toast.success(
        <ToastBody
          title={t("contracts.termsHistory.discardAmendment.success")}
          actions={
            <IconButton
              variant="iconNoBg"
              size="sm"
              onClick={() => toast.dismiss()}
              aria-label={t("close")}
            >
              <Close />
            </IconButton>
          }
        />
      );
    } catch (error) {
      if (isWithdrawVersionNotPending(error)) {
        setTarget(null);
        refetchVersions();
        toast.error(
          t("contracts.termsHistory.discardAmendment.errorNoLongerPending")
        );
      } else {
        toast.error(t("contracts.termsHistory.discardAmendment.error"));
      }
    } finally {
      setIsDiscarding(false);
    }
  }, [target, isDiscarding, contractId, refetchVersions, t]);

  return useMemo(
    () => ({
      target,
      isDiscarding,
      request: setTarget,
      keep: () => {
        if (!isDiscarding) setTarget(null);
      },
      confirm
    }),
    [target, isDiscarding, confirm]
  );
};
